<template>
  <div class="p-0">
    <router-link to="/leave/admin/articles" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900 mb-5">
      <i class="fa-solid fa-arrow-left mr-2"></i>
      Volver a articulos
    </router-link>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="alert-error">
      {{ error }}
      <button type="button" class="ml-2 underline font-semibold" @click="reload">Reintentar</button>
    </div>

    <div v-else>
      <header class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
        <div>
          <p class="breadcrumb">Licencias y permisos / Administracion / Articulos / Detalle</p>
          <div class="flex flex-wrap items-center gap-2">
            <h1 class="text-2xl font-bold text-gray-900">{{ article.code }} - {{ article.name }}</h1>
            <span class="chip" :class="article.active === false ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-100'">
              {{ article.active === false ? 'Inactivo' : 'Activo' }}
            </span>
          </div>
          <p class="mt-1 text-sm text-gray-600">{{ categoryLabel(article.category) }} · Art. {{ article.article_number || '-' }}</p>
        </div>
        <router-link :to="`/leave/admin/articles/${articleId}/edit`" class="primary-button">
          <i class="fa-solid fa-pen mr-2"></i>
          Editar articulo
        </router-link>
      </header>

      <section class="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div class="flag-card">
          <span>Pagado</span>
          <strong>{{ yesNo(article.paid) }}</strong>
        </div>
        <div class="flag-card">
          <span>Documentacion</span>
          <strong>{{ yesNo(article.requires_documentation) }}</strong>
        </div>
        <div class="flag-card">
          <span>Superposicion</span>
          <strong>{{ yesNo(article.can_overlap) }}</strong>
        </div>
        <div class="flag-card">
          <span>Activo</span>
          <strong>{{ yesNo(article.active !== false) }}</strong>
        </div>
      </section>

      <section class="panel">
        <nav class="detail-tabs">
          <button type="button" class="detail-tab" :class="activeTab === 'rules' ? 'detail-tab-active' : ''" @click="activeTab = 'rules'">
            Reglas ({{ rules.length }})
          </button>
          <button type="button" class="detail-tab" :class="activeTab === 'requirements' ? 'detail-tab-active' : ''" @click="activeTab = 'requirements'">
            Requisitos catalogo ({{ requirements.length }})
          </button>
        </nav>

        <div v-if="activeTab === 'rules'" class="p-5">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
            <div>
              <h2 class="text-lg font-bold text-gray-900">Reglas de cupo y comportamiento</h2>
              <p class="text-sm text-gray-600">Campos documentados en `UpsertLeaveArticleRule`.</p>
            </div>
            <button type="button" class="primary-button" @click="openRuleModal()">
              <i class="fa-solid fa-plus mr-2"></i>
              Nueva regla
            </button>
          </div>

          <label class="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
            <input v-model="showAllRules" type="checkbox" class="rounded border-gray-300 text-indigo-700" @change="fetchRules" />
            Mostrar inactivas
          </label>

          <div v-if="rules.length === 0" class="empty-inline">No hay reglas configuradas.</div>
          <div v-else class="space-y-3">
            <article v-for="rule in rules" :key="itemId(rule)" class="sub-card">
              <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <h3 class="font-bold text-gray-900">Periodo: {{ periodLabel(rule.period_type) }}</h3>
                    <span class="chip" :class="rule.active === false ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-100'">
                      {{ rule.active === false ? 'Inactiva' : 'Activa' }}
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-gray-600">{{ ruleSummary(rule) }}</p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button type="button" class="small-secondary-button" @click="openRuleModal(rule)">Editar</button>
                  <button v-if="rule.active !== false" type="button" class="small-danger-button" @click="deactivateRule(rule)">Desactivar</button>
                </div>
              </div>
            </article>
          </div>
        </div>

        <div v-else class="p-5">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
            <div>
              <h2 class="text-lg font-bold text-gray-900">Requisitos del articulo</h2>
              <p class="text-sm text-gray-600">Se copian a cada solicitud nueva como checks.</p>
            </div>
            <button type="button" class="primary-button" @click="openRequirementModal()">
              <i class="fa-solid fa-plus mr-2"></i>
              Nuevo requisito
            </button>
          </div>

          <div v-if="requirements.length === 0" class="empty-inline">No hay requisitos configurados.</div>
          <div v-else class="space-y-3">
            <article v-for="requirement in requirements" :key="itemId(requirement)" class="sub-card">
              <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <div class="flex flex-wrap items-center gap-2">
                    <span class="code-pill">{{ requirement.code }}</span>
                    <h3 class="font-bold text-gray-900">{{ requirement.name }}</h3>
                    <span v-if="requirement.is_required" class="chip bg-indigo-50 text-indigo-700 border-indigo-100">Obligatorio</span>
                    <span class="chip" :class="requirement.active === false ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-green-50 text-green-700 border-green-100'">
                      {{ requirement.active === false ? 'Inactivo' : 'Activo' }}
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-gray-600">Tipo: {{ requirementTypeLabel(requirement.requirement_type) }}</p>
                  <p v-if="requirement.description" class="mt-2 text-sm text-gray-500">{{ requirement.description }}</p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button type="button" class="small-secondary-button" @click="openRequirementModal(requirement)">Editar</button>
                  <button v-if="requirement.active !== false" type="button" class="small-danger-button" @click="deactivateRequirement(requirement)">Desactivar</button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <aside class="mt-5 info-box">
        <i class="fa-solid fa-circle-info text-indigo-700 mt-1"></i>
        <p class="text-sm text-gray-600">La UI muestra solo campos documentados en `openapi.yaml`. Otros controles del diseno quedan fuera hasta que exista contrato API.</p>
      </aside>
    </div>

    <div v-if="ruleModal.open" class="modal-backdrop">
      <form class="modal-card" @submit.prevent="saveRule">
        <div class="modal-header">
          <h2 class="text-lg font-bold">{{ ruleModal.id ? 'Editar regla' : 'Nueva regla' }}</h2>
          <button type="button" class="text-white text-opacity-80 hover:text-white" @click="closeRuleModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5 space-y-4">
          <div>
            <label class="label">Tipo de periodo</label>
            <select v-model="ruleForm.period_type" class="field">
              <option value="">Sin especificar</option>
              <option v-for="period in periodTypes" :key="period.value" :value="period.value">{{ period.label }}</option>
            </select>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="label">Max dias</label>
              <input v-model="ruleForm.max_days" type="number" step="0.01" class="field" />
            </div>
            <div>
              <label class="label">Limite anual dias</label>
              <input v-model="ruleForm.annual_limit_days" type="number" step="0.01" class="field" />
            </div>
            <div>
              <label class="label">Acumulacion mensual dias</label>
              <input v-model="ruleForm.monthly_accrual_days" type="number" step="0.01" class="field" />
            </div>
          </div>
          <div class="checkbox-panel">
            <label class="check-line">
              <input v-model="ruleForm.requires_perfect_attendance" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Requiere asistencia perfecta
            </label>
            <label class="check-line">
              <input v-model="ruleForm.requires_perfect_punctuality" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Requiere puntualidad perfecta
            </label>
            <label class="check-line">
              <input v-model="ruleForm.count_pending_as_used" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Contar pendientes como usados
            </label>
            <label class="check-line">
              <input v-model="ruleForm.active" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Activa
            </label>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="secondary-button" @click="closeRuleModal">Cancelar</button>
          <button type="submit" :disabled="saving" class="primary-button disabled:opacity-50">{{ saving ? 'Guardando...' : 'Guardar regla' }}</button>
        </div>
      </form>
    </div>

    <div v-if="requirementModal.open" class="modal-backdrop">
      <form class="modal-card" @submit.prevent="saveRequirement">
        <div class="modal-header">
          <h2 class="text-lg font-bold">{{ requirementModal.id ? 'Editar requisito' : 'Nuevo requisito' }}</h2>
          <button type="button" class="text-white text-opacity-80 hover:text-white" @click="closeRequirementModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="p-5 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="label">Codigo *</label>
              <input v-model.trim="requirementForm.code" required class="field" />
            </div>
            <div>
              <label class="label">Nombre *</label>
              <input v-model.trim="requirementForm.name" required class="field" />
            </div>
          </div>
          <div>
            <label class="label">Tipo *</label>
            <select v-model="requirementForm.requirement_type" required class="field">
              <option value="">Seleccionar tipo</option>
              <option v-for="type in requirementTypes" :key="type.value" :value="type.value">{{ type.label }}</option>
            </select>
          </div>
          <div>
            <label class="label">Descripcion</label>
            <textarea v-model.trim="requirementForm.description" rows="4" class="field"></textarea>
          </div>
          <div class="checkbox-panel">
            <label class="check-line">
              <input v-model="requirementForm.is_required" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Obligatorio
            </label>
            <label class="check-line">
              <input v-model="requirementForm.active" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
              Activo
            </label>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="secondary-button" @click="closeRequirementModal">Cancelar</button>
          <button type="submit" :disabled="saving" class="primary-button disabled:opacity-50">{{ saving ? 'Guardando...' : 'Guardar requisito' }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import {
  leaveArticleRequirementsApi,
  leaveArticleRulesApi,
  leaveArticlesApi,
} from '@/service/leave.service';
import { categoryLabel, unwrapData, unwrapList } from '@/utils/leave';

const periodTypes = [
  { value: 'calendar_year', label: 'Anio calendario' },
  { value: 'school_year', label: 'Anio lectivo' },
  { value: 'month', label: 'Mes' },
  { value: 'event', label: 'Evento' },
  { value: 'no_limit', label: 'Sin limite' },
];

const requirementTypes = [
  { value: 'boolean', label: 'Booleano' },
  { value: 'document', label: 'Documento' },
  { value: 'validation_rule', label: 'Regla de validacion' },
  { value: 'approval', label: 'Aprobacion' },
  { value: 'attendance', label: 'Asistencia' },
  { value: 'notice', label: 'Aviso' },
  { value: 'medical_board', label: 'Junta medica' },
];

const emptyRuleForm = () => ({
  period_type: '',
  max_days: '',
  annual_limit_days: '',
  monthly_accrual_days: '',
  requires_perfect_attendance: false,
  requires_perfect_punctuality: false,
  count_pending_as_used: true,
  active: true,
});

const emptyRequirementForm = () => ({
  code: '',
  name: '',
  description: '',
  requirement_type: '',
  is_required: true,
  active: true,
});

export default {
  name: 'LeaveAdminArticleDetailView',
  components: { Loading },
  data() {
    return {
      article: {},
      rules: [],
      requirements: [],
      activeTab: 'rules',
      showAllRules: true,
      loading: false,
      saving: false,
      error: '',
      ruleModal: { open: false, id: null },
      requirementModal: { open: false, id: null },
      ruleForm: emptyRuleForm(),
      requirementForm: emptyRequirementForm(),
      periodTypes,
      requirementTypes,
    };
  },
  computed: {
    articleId() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    categoryLabel,
    async reload() {
      this.loading = true;
      this.error = '';
      try {
        await Promise.all([this.fetchArticle(), this.fetchRules(), this.fetchRequirements()]);
      } finally {
        this.loading = false;
      }
    },
    async fetchArticle() {
      try {
        const response = await leaveArticlesApi.get(this.articleId);
        this.article = unwrapData(response) || {};
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar el articulo.';
      }
    },
    async fetchRules() {
      try {
        const response = await leaveArticlesApi.listRules(this.articleId, this.showAllRules ? { all: true } : {});
        this.rules = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar las reglas.';
      }
    },
    async fetchRequirements() {
      try {
        const response = await leaveArticlesApi.listRequirements(this.articleId);
        this.requirements = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los requisitos.';
      }
    },
    itemId(item) {
      return item.id || item.rule_id || item.requirement_id;
    },
    yesNo(value) {
      return value ? 'Si' : 'No';
    },
    periodLabel(value) {
      return periodTypes.find((period) => period.value === value)?.label || value || 'Sin especificar';
    },
    requirementTypeLabel(value) {
      return requirementTypes.find((type) => type.value === value)?.label || value || '-';
    },
    ruleSummary(rule) {
      const parts = [
        rule.max_days !== null && rule.max_days !== undefined ? `Max dias: ${rule.max_days}` : '',
        rule.annual_limit_days !== null && rule.annual_limit_days !== undefined ? `Limite anual: ${rule.annual_limit_days}` : '',
        rule.monthly_accrual_days !== null && rule.monthly_accrual_days !== undefined ? `Acumulacion mensual: ${rule.monthly_accrual_days}` : '',
        rule.requires_perfect_attendance ? 'Asistencia perfecta' : '',
        rule.requires_perfect_punctuality ? 'Puntualidad perfecta' : '',
        rule.count_pending_as_used ? 'Pendientes cuentan como usados' : '',
      ].filter(Boolean);
      return parts.join(' · ') || 'Sin parametros numericos configurados';
    },
    openRuleModal(rule = null) {
      this.ruleModal = { open: true, id: rule ? this.itemId(rule) : null };
      this.ruleForm = rule
        ? {
            ...emptyRuleForm(),
            ...rule,
            max_days: rule.max_days ?? '',
            annual_limit_days: rule.annual_limit_days ?? '',
            monthly_accrual_days: rule.monthly_accrual_days ?? '',
            active: rule.active !== false,
          }
        : emptyRuleForm();
    },
    closeRuleModal() {
      this.ruleModal = { open: false, id: null };
      this.ruleForm = emptyRuleForm();
    },
    rulePayload() {
      const payload = {
        requires_perfect_attendance: Boolean(this.ruleForm.requires_perfect_attendance),
        requires_perfect_punctuality: Boolean(this.ruleForm.requires_perfect_punctuality),
        count_pending_as_used: Boolean(this.ruleForm.count_pending_as_used),
        active: Boolean(this.ruleForm.active),
      };
      if (this.ruleForm.period_type) payload.period_type = this.ruleForm.period_type;
      ['max_days', 'annual_limit_days', 'monthly_accrual_days'].forEach((field) => {
        if (this.ruleForm[field] !== '' && this.ruleForm[field] !== null && this.ruleForm[field] !== undefined) {
          payload[field] = Number(this.ruleForm[field]);
        }
      });
      return payload;
    },
    async saveRule() {
      this.saving = true;
      this.error = '';
      try {
        if (this.ruleModal.id) {
          await leaveArticleRulesApi.update(this.ruleModal.id, this.rulePayload());
        } else {
          await leaveArticlesApi.createRule(this.articleId, this.rulePayload());
        }
        this.closeRuleModal();
        await this.fetchRules();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo guardar la regla.';
      } finally {
        this.saving = false;
      }
    },
    async deactivateRule(rule) {
      if (!window.confirm('Desactivar esta regla de cupo?')) return;
      this.saving = true;
      try {
        await leaveArticleRulesApi.deactivate(this.itemId(rule));
        await this.fetchRules();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo desactivar la regla.';
      } finally {
        this.saving = false;
      }
    },
    openRequirementModal(requirement = null) {
      this.requirementModal = { open: true, id: requirement ? this.itemId(requirement) : null };
      this.requirementForm = requirement
        ? {
            ...emptyRequirementForm(),
            ...requirement,
            description: requirement.description || '',
            active: requirement.active !== false,
          }
        : emptyRequirementForm();
    },
    closeRequirementModal() {
      this.requirementModal = { open: false, id: null };
      this.requirementForm = emptyRequirementForm();
    },
    requirementPayload() {
      return {
        code: this.requirementForm.code,
        name: this.requirementForm.name,
        description: this.requirementForm.description || '',
        requirement_type: this.requirementForm.requirement_type,
        is_required: Boolean(this.requirementForm.is_required),
        active: Boolean(this.requirementForm.active),
      };
    },
    async saveRequirement() {
      this.saving = true;
      this.error = '';
      try {
        if (this.requirementModal.id) {
          await leaveArticleRequirementsApi.update(this.requirementModal.id, this.requirementPayload());
        } else {
          await leaveArticlesApi.createRequirement(this.articleId, this.requirementPayload());
        }
        this.closeRequirementModal();
        await this.fetchRequirements();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo guardar el requisito.';
      } finally {
        this.saving = false;
      }
    },
    async deactivateRequirement(requirement) {
      if (!window.confirm('Desactivar este requisito?')) return;
      this.saving = true;
      try {
        await leaveArticleRequirementsApi.deactivate(this.itemId(requirement));
        await this.fetchRequirements();
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo desactivar el requisito.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2;
}
.panel {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden;
}
.flag-card {
  @apply bg-white border border-gray-200 rounded-lg shadow-sm p-4;
}
.flag-card span {
  @apply block text-xs uppercase tracking-wide text-gray-500;
}
.flag-card strong {
  @apply block mt-1 text-gray-900;
}
.detail-tabs {
  @apply border-b border-gray-200 px-5 pt-4 flex flex-wrap gap-1;
}
.detail-tab {
  @apply px-4 py-3 text-sm font-semibold text-gray-600 border-b-2 border-transparent hover:text-indigo-800;
}
.detail-tab-active {
  @apply text-indigo-900 border-indigo-900;
}
.sub-card {
  @apply bg-white border border-gray-200 rounded-lg p-4;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.checkbox-panel {
  @apply bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-3;
}
.check-line {
  @apply flex items-center gap-2 text-sm font-semibold text-gray-700;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.small-secondary-button {
  @apply inline-flex items-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200;
}
.small-danger-button {
  @apply inline-flex items-center px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 rounded-md hover:bg-red-100;
}
.chip {
  @apply inline-flex px-2 py-1 text-xs font-semibold rounded-full border;
}
.code-pill {
  @apply inline-flex px-2 py-1 rounded bg-gray-100 text-xs font-mono text-gray-600;
}
.alert-error {
  @apply bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md;
}
.empty-inline {
  @apply bg-gray-50 border border-dashed border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500;
}
.info-box {
  @apply bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4 flex gap-3;
}
.modal-backdrop {
  @apply fixed inset-0 z-40 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4;
}
.modal-card {
  @apply bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden;
}
.modal-header {
  @apply bg-indigo-950 text-white px-5 py-4 flex items-center justify-between;
}
.modal-actions {
  @apply bg-gray-50 px-5 py-4 flex justify-end gap-3;
}
</style>
